import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X,
  Zap,
  BarChart3,
  DollarSign,
  Heart,
  MessageCircle,
  Share2,
  Eye,
  ExternalLink,
  Package,
  TrendingUp,
  Instagram,
  Facebook,
  Twitter,
  Youtube,
  Linkedin,
  Hash,
  FileText,
  Loader2,
  Info,
} from 'lucide-react';
import TikTokIcon from '../ui/TikTokIcon';
import { Card, Tooltip } from '../ui';
import { Activation, ActivationPerformance } from '@/types/activation';
import { activationService } from '@/services/activationService';
import { reportService } from '@/services/reportService';
import useGlobalCustomerStore from '@/stores/globalCustomerStore';
import { useToast } from '@/contexts/ToastContext';

interface ActivationInsightsDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  activation: Activation | null;
}

const ActivationInsightsDrawer: React.FC<ActivationInsightsDrawerProps> = ({
  isOpen,
  onClose,
  activation,
}) => {
  const [activeTab, setActiveTab] = useState<'overview' | 'posts'>('overview');
  const [loading, setLoading] = useState(false);
  const [performance, setPerformance] = useState<ActivationPerformance | null>(null);
  const [isGeneratingReport, setIsGeneratingReport] = useState(false);

  // Expansion state for breakdown sections
  const [expandedSections, setExpandedSections] = useState<{
    assets: boolean;
    hashtags: boolean;
    formats: boolean;
  }>({ assets: false, hashtags: false, formats: false });

  const { selectedCustomer } = useGlobalCustomerStore();
  const { showSuccess, showError } = useToast();

  // Load performance data when activation changes
  useEffect(() => {
    if (activation?.id && selectedCustomer?.id && isOpen) {
      loadPerformanceData();
    }
  }, [activation?.id, selectedCustomer?.id, isOpen]);

  // Reset tab and expanded sections when drawer opens
  useEffect(() => {
    if (isOpen) {
      setActiveTab('overview');
      setExpandedSections({ assets: false, hashtags: false, formats: false });
    }
  }, [isOpen]);

  const loadPerformanceData = async () => {
    if (!activation?.id || !selectedCustomer?.id) return;

    try {
      setLoading(true);
      const data = await activationService.getActivationPerformance(
        activation.id,
        String(selectedCustomer.id)
      );
      setPerformance(data);
    } catch (error) {
      console.error('Failed to load activation performance:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatNumber = (num: number) => {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M';
    } else if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K';
    }
    return num.toString();
  };

  const formatCurrency = (num: number) => {
    return num.toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const truncateCaption = (caption: string | null, maxLength: number = 150) => {
    if (!caption) return '';
    if (caption.length <= maxLength) return caption;
    return caption.substring(0, maxLength) + '...';
  };

  const getStatusBadgeClasses = (status: string) => {
    switch (status) {
      case 'Active':
        return 'bg-green-100 text-green-800';
      case 'Completed':
        return 'bg-blue-100 text-blue-800';
      case 'Pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'Suspended':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Platform icon helper - simplified for posts
  const getPlatformFromUrl = (url: string): number => {
    if (url.includes('instagram')) return 1;
    if (url.includes('tiktok')) return 2;
    if (url.includes('facebook')) return 3;
    if (url.includes('twitter') || url.includes('x.com')) return 4;
    if (url.includes('youtube')) return 5;
    if (url.includes('linkedin')) return 6;
    return 1; // default
  };

  const getPlatformIcon = (platformId: number) => {
    switch (platformId) {
      case 1:
        return Instagram;
      case 2:
        return TikTokIcon;
      case 3:
        return Facebook;
      case 4:
        return Twitter;
      case 5:
        return Youtube;
      case 6:
        return Linkedin;
      default:
        return Instagram;
    }
  };

  const getPlatformColor = (platformId: number) => {
    switch (platformId) {
      case 1:
        return 'text-pink-500';
      case 2:
        return 'text-black';
      case 3:
        return 'text-blue-600';
      case 4:
        return 'text-blue-400';
      case 5:
        return 'text-red-600';
      case 6:
        return 'text-blue-700';
      default:
        return 'text-gray-500';
    }
  };

  // Calculate delivery progress
  const dealValue = performance?.deal_value || activation?.deal_value || 0;
  const totalEmv = performance?.metrics?.total_emv || 0;
  const progressPercentage = dealValue > 0 ? Math.min(Math.round((totalEmv / dealValue) * 100), 100) : 0;

  // Generate PDF report
  const handleGenerateReport = async () => {
    if (!activation?.id || !selectedCustomer?.id) return;

    setIsGeneratingReport(true);
    try {
      await reportService.generateReport({
        customerId: String(selectedCustomer.id),
        reportType: 'activation-detail-report',
        activationId: activation.id,
        // Use activation's date range if available, otherwise use last 30 days
        period: activation.start_date && activation.end_date ? 'custom' : '30days',
        ...(activation.start_date && activation.end_date && {
          customStartDate: activation.start_date.split('T')[0],
          customEndDate: activation.end_date.split('T')[0]
        })
      });
      showSuccess('Report Generated', 'The activation report has been downloaded');
    } catch (error) {
      console.error('Failed to generate report:', error);
      showError(
        'Report Generation Failed',
        error instanceof Error ? error.message : 'Failed to generate report'
      );
    } finally {
      setIsGeneratingReport(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 z-40"
            onClick={onClose}
          />

          {/* Drawer */}
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'tween', duration: 0.3 }}
            className="fixed right-0 top-0 h-full w-full max-w-2xl bg-white shadow-xl z-50 flex flex-col"
          >
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg">
                  <Zap className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">{activation?.name}</h2>
                  <p className="text-sm text-gray-500">Activation Insights</p>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                {activation?.status && (
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusBadgeClasses(activation.status)}`}>
                    {activation.status}
                  </span>
                )}
                <button
                  onClick={onClose}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <X className="h-5 w-5 text-gray-400" />
                </button>
              </div>
            </div>

            {/* Tabs */}
            <div className="flex items-center justify-between border-b border-gray-200 px-4">
              <div className="flex">
                <button
                  onClick={() => setActiveTab('overview')}
                  className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === 'overview'
                      ? 'border-blue-600 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Overview
                </button>
                <button
                  onClick={() => setActiveTab('posts')}
                  className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === 'posts'
                      ? 'border-blue-600 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Posts ({performance?.top_posts?.length || 0})
                </button>
              </div>
              <button
                onClick={handleGenerateReport}
                disabled={isGeneratingReport || !activation?.id}
                className="flex items-center px-3 py-1.5 bg-gradient-to-r from-blue-600 to-blue-700 text-white text-xs font-medium rounded-lg hover:from-blue-700 hover:to-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-200 shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
                title="Download activation report as PDF"
              >
                {isGeneratingReport ? (
                  <>
                    <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <FileText className="h-3.5 w-3.5 mr-1.5" />
                    Download Report
                  </>
                )}
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto">
              {loading && (
                <div className="flex items-center justify-center h-64">
                  <div className="flex items-center space-x-3">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                    <span className="text-xs text-gray-900">Loading performance data...</span>
                  </div>
                </div>
              )}

              {!loading && activeTab === 'overview' && (
                <div className="p-6 space-y-4">
                  {/* Value Delivery */}
                  {dealValue > 0 && (
                    <Card className="p-4 bg-gradient-to-br from-slate-50 to-gray-100 border-slate-200">
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="text-sm font-semibold text-gray-900">Value Delivery</h3>
                        <TrendingUp className="h-4 w-4 text-gray-500" />
                      </div>
                      <div className="flex items-end justify-between mb-2">
                        <div>
                          <p className="text-2xl font-bold text-gray-900">
                            {formatCurrency(totalEmv)}
                          </p>
                          <p className="text-xs text-gray-500">
                            of {formatCurrency(dealValue)} deal value
                          </p>
                        </div>
                        <div className="text-right">
                          <p className={`text-xl font-bold ${
                            progressPercentage >= 80 ? 'text-green-600' :
                            progressPercentage >= 50 ? 'text-yellow-600' :
                            'text-red-600'
                          }`}>
                            {progressPercentage}%
                          </p>
                        </div>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className={`h-2 rounded-full ${
                            progressPercentage >= 80 ? 'bg-green-500' :
                            progressPercentage >= 50 ? 'bg-yellow-500' :
                            'bg-red-500'
                          }`}
                          style={{ width: `${progressPercentage}%` }}
                        />
                      </div>
                    </Card>
                  )}

                  {/* Performance Overview */}
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900 mb-3">Performance Overview</h3>
                    <div className="grid grid-cols-2 gap-3">
                      <Card className="px-3 py-2">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-xs font-medium text-gray-500 flex items-center gap-1">
                              Total Posts
                              <Tooltip
                                content="Unique posts that matched at least one asset in this activation. Each post is counted once, even if it matches multiple assets."
                                placement="top"
                                maxWidth={220}
                              >
                                <Info className="h-3 w-3 text-gray-400 hover:text-gray-600 cursor-help" />
                              </Tooltip>
                            </p>
                            <p className="text-base font-bold text-gray-900">
                              {formatNumber(performance?.metrics?.total_posts || 0)}
                            </p>
                          </div>
                          <div className="p-1.5 bg-blue-100 rounded-md">
                            <BarChart3 className="h-3.5 w-3.5 text-blue-600" />
                          </div>
                        </div>
                      </Card>

                      <Card className="px-3 py-2">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-xs font-medium text-gray-500 flex items-center gap-1">
                              Total EMV
                              <Tooltip
                                content="Earned Media Value of all matched posts. Each post's EMV is counted once. Note: The sum of EMV by asset may be higher because posts matching multiple assets are credited to each asset."
                                placement="top"
                                maxWidth={240}
                              >
                                <Info className="h-3 w-3 text-gray-400 hover:text-gray-600 cursor-help" />
                              </Tooltip>
                            </p>
                            <p className="text-base font-bold text-green-600">
                              {formatCurrency(totalEmv)}
                            </p>
                          </div>
                          <div className="p-1.5 bg-green-100 rounded-md">
                            <DollarSign className="h-3.5 w-3.5 text-green-600" />
                          </div>
                        </div>
                      </Card>

                      <Card className="px-3 py-2">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-xs font-medium text-gray-500 flex items-center gap-1">
                              Total Views
                              <Tooltip
                                content="Combined view count from all matched posts. Views are deduplicated the same way as posts."
                                placement="top"
                                maxWidth={200}
                              >
                                <Info className="h-3 w-3 text-gray-400 hover:text-gray-600 cursor-help" />
                              </Tooltip>
                            </p>
                            <p className="text-base font-bold text-gray-900">
                              {formatNumber(performance?.metrics?.total_views || 0)}
                            </p>
                          </div>
                          <div className="p-1.5 bg-purple-100 rounded-md">
                            <Eye className="h-3.5 w-3.5 text-purple-600" />
                          </div>
                        </div>
                      </Card>

                      <Card className="px-3 py-2">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-xs font-medium text-gray-500 flex items-center gap-1">
                              Total Likes
                              <Tooltip
                                content="Combined like count from all matched posts."
                                placement="top"
                                maxWidth={180}
                              >
                                <Info className="h-3 w-3 text-gray-400 hover:text-gray-600 cursor-help" />
                              </Tooltip>
                            </p>
                            <p className="text-base font-bold text-gray-900">
                              {formatNumber(performance?.metrics?.total_likes || 0)}
                            </p>
                          </div>
                          <div className="p-1.5 bg-red-100 rounded-md">
                            <Heart className="h-3.5 w-3.5 text-red-600" />
                          </div>
                        </div>
                      </Card>

                      <Card className="px-3 py-2">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-xs font-medium text-gray-500 flex items-center gap-1">
                              Comments
                              <Tooltip
                                content="Combined comment count from all matched posts."
                                placement="top"
                                maxWidth={180}
                              >
                                <Info className="h-3 w-3 text-gray-400 hover:text-gray-600 cursor-help" />
                              </Tooltip>
                            </p>
                            <p className="text-base font-bold text-gray-900">
                              {formatNumber(performance?.metrics?.total_comments || 0)}
                            </p>
                          </div>
                          <div className="p-1.5 bg-blue-100 rounded-md">
                            <MessageCircle className="h-3.5 w-3.5 text-blue-600" />
                          </div>
                        </div>
                      </Card>

                      <Card className="px-3 py-2">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-xs font-medium text-gray-500 flex items-center gap-1">
                              Shares
                              <Tooltip
                                content="Combined share count from all matched posts."
                                placement="top"
                                maxWidth={180}
                              >
                                <Info className="h-3 w-3 text-gray-400 hover:text-gray-600 cursor-help" />
                              </Tooltip>
                            </p>
                            <p className="text-base font-bold text-gray-900">
                              {formatNumber(performance?.metrics?.total_shares || 0)}
                            </p>
                          </div>
                          <div className="p-1.5 bg-green-100 rounded-md">
                            <Share2 className="h-3.5 w-3.5 text-green-600" />
                          </div>
                        </div>
                      </Card>
                    </div>
                  </div>

                  {/* EMV Contribution Breakdown */}
                  {((performance?.assets && performance.assets.length > 0) ||
                    (performance?.hashtags && performance.hashtags.length > 0) ||
                    (performance?.formats && performance.formats.length > 0)) && (
                    <Card className="p-4">
                      <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-1.5">
                        EMV Contribution Breakdown
                        <Tooltip
                          content="Shows how EMV is attributed across assets, tags, and formats. A single post can match multiple assets/tags, so each gets full credit. This means the sum may exceed Total EMV."
                          placement="top"
                          maxWidth={260}
                        >
                          <Info className="h-3.5 w-3.5 text-gray-400 hover:text-gray-600 cursor-help" />
                        </Tooltip>
                      </h3>
                      <div className="space-y-4">
                        {/* By Asset */}
                        {performance?.assets && performance.assets.length > 0 && (
                          <div>
                            <div className="flex items-center justify-between mb-2">
                              <p className="text-xs font-medium text-gray-600 flex items-center space-x-1">
                                <Package className="h-3 w-3" />
                                <span>By Asset</span>
                                <Tooltip
                                  content="Each asset receives full credit for posts it contributed to. Posts matching multiple assets are counted in each."
                                  placement="top"
                                  maxWidth={220}
                                >
                                  <Info className="h-2.5 w-2.5 text-gray-400 hover:text-gray-600 cursor-help" />
                                </Tooltip>
                              </p>
                              <span className="text-xs font-semibold text-green-600">
                                {formatCurrency(performance.assets.reduce((sum, a) => sum + a.emv, 0))}
                              </span>
                            </div>
                            <div className="space-y-2">
                              {(expandedSections.assets ? performance.assets : performance.assets.slice(0, 4)).map((asset, index) => {
                                const colors = ['#10B981', '#34D399', '#6EE7B7', '#A7F3D0'];
                                const maxEmv = Math.max(...performance.assets.map(a => a.emv), 1);
                                const pct = (asset.emv / maxEmv) * 100;
                                return (
                                  <div key={asset.asset_id}>
                                    <div className="flex justify-between text-xs mb-0.5">
                                      <span className="text-gray-700 truncate" style={{ maxWidth: '60%' }}>{asset.asset_name}</span>
                                      <span className="text-gray-500">{formatCurrency(asset.emv)}</span>
                                    </div>
                                    <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                                      <div
                                        className="h-full rounded-full"
                                        style={{ width: `${pct}%`, backgroundColor: colors[index % colors.length] }}
                                      />
                                    </div>
                                  </div>
                                );
                              })}
                              {performance.assets.length > 4 && (
                                <button
                                  onClick={() => setExpandedSections(prev => ({ ...prev, assets: !prev.assets }))}
                                  className="text-xs text-blue-600 hover:text-blue-700 font-medium mt-1"
                                >
                                  {expandedSections.assets ? 'Show less' : `+${performance.assets.length - 4} more assets`}
                                </button>
                              )}
                            </div>
                          </div>
                        )}

                        {/* By Attribution Tag */}
                        {performance?.hashtags && performance.hashtags.length > 0 && (
                          <div>
                            <div className="flex items-center justify-between mb-2">
                              <p className="text-xs font-medium text-gray-600 flex items-center space-x-1">
                                <Hash className="h-3 w-3" />
                                <span>By Attribution Tag</span>
                              </p>
                              <span className="text-xs font-semibold text-green-600">
                                {formatCurrency(performance.hashtags.reduce((sum, h) => sum + h.emv, 0))}
                              </span>
                            </div>
                            <div className="space-y-2">
                              {(expandedSections.hashtags ? performance.hashtags : performance.hashtags.slice(0, 4)).map((hashtag, index) => {
                                const colors = ['#F59E0B', '#FBBF24', '#FCD34D', '#FDE68A'];
                                const maxEmv = Math.max(...performance.hashtags.map(h => h.emv), 1);
                                const pct = (hashtag.emv / maxEmv) * 100;
                                return (
                                  <div key={hashtag.hashtag_id}>
                                    <div className="flex justify-between text-xs mb-0.5">
                                      <span className="text-gray-700 truncate" style={{ maxWidth: '60%' }}>
                                        {hashtag.hashtag_text.startsWith('@') ? hashtag.hashtag_text : hashtag.hashtag_text.startsWith('#') ? hashtag.hashtag_text : `#${hashtag.hashtag_text}`}
                                      </span>
                                      <span className="text-gray-500">{formatCurrency(hashtag.emv)}</span>
                                    </div>
                                    <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                                      <div
                                        className="h-full rounded-full"
                                        style={{ width: `${pct}%`, backgroundColor: colors[index % colors.length] }}
                                      />
                                    </div>
                                  </div>
                                );
                              })}
                              {performance.hashtags.length > 4 && (
                                <button
                                  onClick={() => setExpandedSections(prev => ({ ...prev, hashtags: !prev.hashtags }))}
                                  className="text-xs text-blue-600 hover:text-blue-700 font-medium mt-1"
                                >
                                  {expandedSections.hashtags ? 'Show less' : `+${performance.hashtags.length - 4} more tags`}
                                </button>
                              )}
                            </div>
                          </div>
                        )}

                        {/* By Format */}
                        {performance?.formats && performance.formats.length > 0 && (
                          <div>
                            <div className="flex items-center justify-between mb-2">
                              <p className="text-xs font-medium text-gray-600 flex items-center space-x-1">
                                <FileText className="h-3 w-3" />
                                <span>By Format</span>
                              </p>
                              <span className="text-xs font-semibold text-green-600">
                                {formatCurrency(performance.formats.reduce((sum, f) => sum + f.emv, 0))}
                              </span>
                            </div>
                            <div className="space-y-2">
                              {(expandedSections.formats ? performance.formats : performance.formats.slice(0, 4)).map((format, index) => {
                                const colors = ['#8B5CF6', '#A78BFA', '#C4B5FD', '#DDD6FE'];
                                const maxEmv = Math.max(...performance.formats.map(f => f.emv), 1);
                                const pct = (format.emv / maxEmv) * 100;
                                return (
                                  <div key={format.format_name}>
                                    <div className="flex justify-between text-xs mb-0.5">
                                      <span className="text-gray-700 truncate" style={{ maxWidth: '60%' }}>
                                        {format.format_name}
                                      </span>
                                      <span className="text-gray-500">{formatCurrency(format.emv)}</span>
                                    </div>
                                    <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                                      <div
                                        className="h-full rounded-full"
                                        style={{ width: `${pct}%`, backgroundColor: colors[index % colors.length] }}
                                      />
                                    </div>
                                  </div>
                                );
                              })}
                              {performance.formats.length > 4 && (
                                <button
                                  onClick={() => setExpandedSections(prev => ({ ...prev, formats: !prev.formats }))}
                                  className="text-xs text-blue-600 hover:text-blue-700 font-medium mt-1"
                                >
                                  {expandedSections.formats ? 'Show less' : `+${performance.formats.length - 4} more formats`}
                                </button>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </Card>
                  )}

                  {/* Date Range Info */}
                  {activation && (activation.start_date || activation.end_date) && (
                    <Card className="px-3 py-2">
                      <h3 className="text-xs font-semibold text-gray-900 mb-1">Campaign Period</h3>
                      <p className="text-xs text-gray-600">
                        {formatDate(activation.start_date)} - {formatDate(activation.end_date)}
                      </p>
                    </Card>
                  )}
                </div>
              )}

              {!loading && activeTab === 'posts' && (
                <div className="p-6">
                  {performance?.top_posts && performance.top_posts.length > 0 ? (
                    <div className="space-y-4">
                      {performance.top_posts.map((post, index) => {
                        const platformId = getPlatformFromUrl(post.post_url);
                        const PlatformIcon = getPlatformIcon(platformId);

                        return (
                          <motion.div
                            key={`${post.partition_key}-${post.post_id}`}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: index * 0.05 }}
                          >
                            <Card className="p-4 hover:shadow-md transition-shadow">
                              <div className="flex items-start space-x-4">
                                <div className="flex-shrink-0">
                                  <div className={`p-2 rounded-lg bg-gray-100 ${getPlatformColor(platformId)}`}>
                                    <PlatformIcon className="h-5 w-5" />
                                  </div>
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center justify-between mb-2">
                                    <div className="flex items-center space-x-2">
                                      <p className="text-xs font-medium text-gray-900">
                                        Post #{index + 1}
                                      </p>
                                    </div>
                                    <div className="flex items-center space-x-2">
                                      <span className="text-sm font-bold text-green-600">
                                        {formatCurrency(post.emv)}
                                      </span>
                                      {post.post_url && (
                                        <a
                                          href={post.post_url}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
                                          onClick={(e) => e.stopPropagation()}
                                        >
                                          <ExternalLink className="h-4 w-4" />
                                        </a>
                                      )}
                                    </div>
                                  </div>
                                  {post.caption && (
                                    <p className="text-xs text-gray-600 mb-3">
                                      {truncateCaption(post.caption)}
                                    </p>
                                  )}
                                  <div className="flex items-center justify-between text-xs text-gray-500">
                                    <div className="flex items-center space-x-4">
                                      <div className="flex items-center space-x-1">
                                        <Eye className="h-3 w-3" />
                                        <span>{formatNumber(post.views)}</span>
                                      </div>
                                      <div className="flex items-center space-x-1">
                                        <Heart className="h-3 w-3" />
                                        <span>{formatNumber(post.likes)}</span>
                                      </div>
                                      <div className="flex items-center space-x-1">
                                        <MessageCircle className="h-3 w-3" />
                                        <span>{formatNumber(post.comments)}</span>
                                      </div>
                                      <div className="flex items-center space-x-1">
                                        <Share2 className="h-3 w-3" />
                                        <span>{formatNumber(post.shares)}</span>
                                      </div>
                                    </div>
                                    <span>{formatDate(post.posted_at)}</span>
                                  </div>
                                </div>
                              </div>
                            </Card>
                          </motion.div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      <BarChart3 className="mx-auto h-10 w-10 text-gray-400" />
                      <h3 className="mt-2 text-sm font-medium text-gray-900">No posts found</h3>
                      <p className="mt-1 text-xs text-gray-500">
                        No posts have been tagged for this activation yet.
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default ActivationInsightsDrawer;
